// Tickets page JavaScript

import {
    apiRequest,
    initAuthenticatedPage,
    getDefaultProjectId,
    setDefaultProjectId,
    getHiddenVersionIds,
    setHiddenVersionIds,
} from "./common";

// Types
interface Project {
    id: number;
    name: string;
    key: string;
}

interface Tag {
    id: number;
    project_id: number;
    name: string;
    created_at: string;
}

interface FixVersion {
    id: number;
    project_id: number;
    version_number: string;
    release_date: string | null;
    created_at: string;
    updated_at: string;
}

interface Ticket {
    id: number;
    project_id: number;
    identifier: string;
    title: string;
    description: string | null;
    status: TicketStatus;
    fix_version_id: number | null;
    planning_notes: string | null;
    relevant_files: string[];
    created_at: string;
    updated_at: string;
    tags: Tag[];
}

type TicketStatus =
    | "todo"
    | "plan_in_review"
    | "planned"
    | "in_development"
    | "in_review"
    | "dev_done"
    | "testing_done"
    | "accepted_for_release"
    | "rejected";

// Status display names
const STATUS_LABELS: Record<TicketStatus, string> = {
    todo: "Todo",
    plan_in_review: "Plan In Review",
    planned: "Planned",
    in_development: "In Development",
    in_review: "In Review",
    dev_done: "Dev Done",
    testing_done: "Testing Done",
    accepted_for_release: "Accepted for Release",
    rejected: "Rejected",
};

// Sort configuration
type SortColumn = "identifier" | "status" | "created_at" | "updated_at";
type SortDirection = "asc" | "desc";

// State
let currentProjectId: number | null = null;
let tickets: Ticket[] = [];
let projectTags: Tag[] = [];
let projectFixVersions: FixVersion[] = [];

// Version visibility state
let hiddenVersionIds: number[] = [];

// Sort state
let sortColumn: SortColumn = "identifier";
let sortDirection: SortDirection = "asc";

// DOM elements
let projectSelect: HTMLSelectElement;
let ticketsContainer: HTMLElement;
let newTicketBtn: HTMLButtonElement;
let manageTagsBtn: HTMLButtonElement;

// Filter elements
let filterStatusSelect: HTMLSelectElement;
let filterFixVersionSelect: HTMLSelectElement;
let filterTagSelect: HTMLSelectElement;
let applyFiltersBtn: HTMLButtonElement;
let clearFiltersBtn: HTMLButtonElement;

// Delete dialog elements
let deleteTicketDialog: HTMLDialogElement;
let deleteTicketName: HTMLElement;
let deleteTicketConfirmBtn: HTMLButtonElement;

// Tags dialog elements
let tagsDialog: HTMLDialogElement;
let newTagNameInput: HTMLInputElement;
let addTagBtn: HTMLButtonElement;
let tagsList: HTMLElement;

function escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
}

function formatDateShort(dateString: string | null): string {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
}

// Initialize
async function initTicketsPage(): Promise<void> {
    // Wait for authentication to complete before making API requests
    const user = await initAuthenticatedPage();
    if (!user) {
        return;
    }

    // Get DOM elements
    projectSelect = document.getElementById(
        "project-select"
    ) as HTMLSelectElement;
    ticketsContainer = document.getElementById(
        "tickets-container"
    ) as HTMLElement;
    newTicketBtn = document.getElementById(
        "new-ticket-btn"
    ) as HTMLButtonElement;
    manageTagsBtn = document.getElementById(
        "manage-tags-btn"
    ) as HTMLButtonElement;

    // Filter elements
    filterStatusSelect = document.getElementById(
        "filter-status"
    ) as HTMLSelectElement;
    filterFixVersionSelect = document.getElementById(
        "filter-fix-version"
    ) as HTMLSelectElement;
    filterTagSelect = document.getElementById(
        "filter-tag"
    ) as HTMLSelectElement;
    applyFiltersBtn = document.getElementById(
        "apply-filters-btn"
    ) as HTMLButtonElement;
    clearFiltersBtn = document.getElementById(
        "clear-filters-btn"
    ) as HTMLButtonElement;

    // Delete dialog elements
    deleteTicketDialog = document.getElementById(
        "delete-ticket-dialog"
    ) as HTMLDialogElement;
    deleteTicketName = document.getElementById(
        "delete-ticket-name"
    ) as HTMLElement;
    deleteTicketConfirmBtn = document.getElementById(
        "delete-ticket-confirm-btn"
    ) as HTMLButtonElement;

    // Tags dialog elements
    tagsDialog = document.getElementById("tags-dialog") as HTMLDialogElement;
    newTagNameInput = document.getElementById(
        "new-tag-name"
    ) as HTMLInputElement;
    addTagBtn = document.getElementById("add-tag-btn") as HTMLButtonElement;
    tagsList = document.getElementById("tags-list") as HTMLElement;

    // Setup event listeners
    setupEventListeners();

    // Check for projectId and fixVersionId in URL params
    const params = new URLSearchParams(window.location.search);
    const urlProjectId = params.get("projectId");
    const urlFixVersionId = params.get("fixVersionId");

    // Load projects, then select if specified (URL param takes precedence over default)
    loadProjects().then(async () => {
        const projectIdToSelect =
            urlProjectId || getDefaultProjectId()?.toString();
        if (projectIdToSelect) {
            // Verify the project exists in the dropdown before selecting
            const optionExists = Array.from(projectSelect.options).some(
                (opt) => opt.value === projectIdToSelect
            );
            if (optionExists) {
                projectSelect.value = projectIdToSelect;
                await onProjectChange();
                // Apply fix version filter if specified (only from URL)
                if (urlFixVersionId) {
                    filterFixVersionSelect.value = urlFixVersionId;
                    await loadTickets();
                }
            }
        }
    });
}

function setupEventListeners(): void {
    // Project selection
    projectSelect.addEventListener("change", onProjectChange);

    // Header buttons
    newTicketBtn.addEventListener("click", () => navigateToEditor(null));
    manageTagsBtn.addEventListener("click", openTagsDialog);

    // Filter buttons
    applyFiltersBtn.addEventListener("click", applyFilters);
    clearFiltersBtn.addEventListener("click", clearFilters);

    // Version visibility toggle links
    document
        .getElementById("version-show-all")
        ?.addEventListener("click", (e) => {
            e.preventDefault();
            hiddenVersionIds = [];
            if (currentProjectId) {
                setHiddenVersionIds(currentProjectId, hiddenVersionIds);
            }
            renderVersionVisibility();
            if (!filterFixVersionSelect.value) {
                applyClientSideVersionFilter();
            }
        });
    document
        .getElementById("version-show-none")
        ?.addEventListener("click", (e) => {
            e.preventDefault();
            hiddenVersionIds = [
                0,
                ...projectFixVersions.map((fv) => fv.id),
            ];
            if (currentProjectId) {
                setHiddenVersionIds(currentProjectId, hiddenVersionIds);
            }
            renderVersionVisibility();
            if (!filterFixVersionSelect.value) {
                applyClientSideVersionFilter();
            }
        });

    // Delete dialog
    document
        .getElementById("delete-ticket-cancel-btn")
        ?.addEventListener("click", closeDeleteDialog);
    deleteTicketConfirmBtn.addEventListener("click", deleteTicket);
    deleteTicketDialog.addEventListener("click", (e) => {
        if (e.target === deleteTicketDialog) closeDeleteDialog();
    });

    // Tags dialog
    document
        .getElementById("tags-dialog-close-btn")
        ?.addEventListener("click", closeTagsDialog);
    document
        .getElementById("tags-dialog-done-btn")
        ?.addEventListener("click", closeTagsDialog);
    addTagBtn.addEventListener("click", addTag);
    newTagNameInput.addEventListener("keypress", (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            addTag();
        }
    });
    tagsDialog.addEventListener("click", (e) => {
        if (e.target === tagsDialog) closeTagsDialog();
    });
}

// Navigation to editor page
function navigateToEditor(ticketId: number | null): void {
    if (!currentProjectId) return;

    const params = new URLSearchParams();
    params.set("projectId", currentProjectId.toString());
    if (ticketId !== null) {
        params.set("ticketId", ticketId.toString());
    }
    window.location.href = `/pages/ticket-edit.html?${params.toString()}`;
}

// Navigation to view page
function navigateToView(ticketId: number): void {
    if (!currentProjectId) return;

    const params = new URLSearchParams();
    params.set("projectId", currentProjectId.toString());
    params.set("ticketId", ticketId.toString());
    window.location.href = `/pages/ticket-view.html?${params.toString()}`;
}

// Project loading and selection
async function loadProjects(): Promise<void> {
    try {
        const projects = await apiRequest<Project[]>("/projects");
        renderProjectOptions(projects);
    } catch (error) {
        console.error("Failed to load projects:", error);
    }
}

function renderProjectOptions(projects: Project[]): void {
    const options = projects.map(
        (p) =>
            `<option value="${p.id}">${escapeHtml(p.name)} (${escapeHtml(p.key)})</option>`
    );
    projectSelect.innerHTML = `<option value="">Select a project...</option>${options.join("")}`;
}

async function onProjectChange(): Promise<void> {
    const projectId = projectSelect.value
        ? parseInt(projectSelect.value, 10)
        : null;
    currentProjectId = projectId;
    tickets = [];

    // Save as default project
    setDefaultProjectId(projectId);

    // Enable/disable buttons
    const hasProject = projectId !== null;
    newTicketBtn.disabled = !hasProject;
    manageTagsBtn.disabled = !hasProject;
    applyFiltersBtn.disabled = !hasProject;
    clearFiltersBtn.disabled = !hasProject;

    if (hasProject) {
        await Promise.all([loadTags(), loadFixVersions()]);
        hiddenVersionIds = getHiddenVersionIds(projectId!);
        populateFilterSelects();
        renderVersionVisibility();
        await loadTickets();
    } else {
        ticketsContainer.innerHTML =
            '<div class="empty-state"><p>Select a project to view tickets</p></div>';
        projectTags = [];
        projectFixVersions = [];
        clearFilterSelects();
    }
}

// Tags
async function loadTags(): Promise<void> {
    if (!currentProjectId) return;

    try {
        projectTags = await apiRequest<Tag[]>(
            `/projects/${currentProjectId}/tags`
        );
    } catch (error) {
        console.error("Failed to load tags:", error);
        projectTags = [];
    }
}

// Fix versions
async function loadFixVersions(): Promise<void> {
    if (!currentProjectId) return;

    try {
        projectFixVersions = await apiRequest<FixVersion[]>(
            `/projects/${currentProjectId}/fix-versions`
        );
    } catch (error) {
        console.error("Failed to load fix versions:", error);
        projectFixVersions = [];
    }
}

// Filter selects
function populateFilterSelects(): void {
    // Fix version filter dropdown (override)
    const fixVersionOptions = projectFixVersions
        .map(
            (fv) =>
                `<option value="${fv.id}">${escapeHtml(fv.version_number)}</option>`
        )
        .join("");
    filterFixVersionSelect.innerHTML = `<option value="">All Visible</option>${fixVersionOptions}`;

    // Tag filter
    const tagOptions = projectTags
        .map((t) => `<option value="${t.id}">${escapeHtml(t.name)}</option>`)
        .join("");
    filterTagSelect.innerHTML = `<option value="">All Tags</option>${tagOptions}`;
}

function clearFilterSelects(): void {
    filterStatusSelect.value = "";
    filterFixVersionSelect.innerHTML = '<option value="">All Visible</option>';
    filterTagSelect.innerHTML = '<option value="">All Tags</option>';
    const visibilityList = document.getElementById("version-visibility-list");
    if (visibilityList) visibilityList.innerHTML = "";
    const visibilityInfo = document.getElementById("version-visibility-info");
    if (visibilityInfo) visibilityInfo.textContent = "";
}

// Version visibility
function renderVersionVisibility(): void {
    const container = document.getElementById("version-visibility-list");
    const infoEl = document.getElementById("version-visibility-info");
    if (!container || !infoEl) return;

    // Build checkbox rows: one per version + "No Version"
    const rows: string[] = [];

    // "No Version" option
    const noVersionHidden = hiddenVersionIds.includes(0);
    rows.push(`
        <label class="version-visibility-item">
            <input type="checkbox" data-version-id="0" ${!noVersionHidden ? "checked" : ""}>
            <span>No Version</span>
        </label>
    `);

    for (const fv of projectFixVersions) {
        const isHidden = hiddenVersionIds.includes(fv.id);
        rows.push(`
            <label class="version-visibility-item">
                <input type="checkbox" data-version-id="${fv.id}" ${!isHidden ? "checked" : ""}>
                <span>${escapeHtml(fv.version_number)}</span>
            </label>
        `);
    }

    container.innerHTML = rows.join("");

    // Attach change listeners
    container.querySelectorAll("input[type=checkbox]").forEach((cb) => {
        cb.addEventListener("change", () => {
            const input = cb as HTMLInputElement;
            const versionId = parseInt(input.dataset.versionId || "0", 10);
            if (input.checked) {
                hiddenVersionIds = hiddenVersionIds.filter(
                    (id) => id !== versionId
                );
            } else {
                hiddenVersionIds.push(versionId);
            }
            if (currentProjectId) {
                setHiddenVersionIds(currentProjectId, hiddenVersionIds);
            }
            updateVersionVisibilityInfo();
            // Re-filter without re-fetching if no specific version override
            if (!filterFixVersionSelect.value) {
                applyClientSideVersionFilter();
            }
        });
    });

    updateVersionVisibilityInfo();
}

function updateVersionVisibilityInfo(): void {
    const infoEl = document.getElementById("version-visibility-info");
    if (!infoEl) return;

    // Total = versions + "No Version"
    const totalCount = projectFixVersions.length + 1;
    const visibleCount = totalCount - hiddenVersionIds.length;

    if (visibleCount < totalCount) {
        infoEl.textContent = `Showing ${visibleCount} of ${totalCount} versions`;
    } else {
        infoEl.textContent = "";
    }
}

function applyClientSideVersionFilter(): void {
    // Re-render from the full fetched tickets with visibility filter
    sortTickets();
    renderTicketsTable();
}

// Tickets loading
async function loadTickets(): Promise<void> {
    if (!currentProjectId) return;

    ticketsContainer.innerHTML =
        '<div class="loading">Loading tickets...</div>';

    try {
        const statusFilter = filterStatusSelect.value;
        const fixVersionFilter = filterFixVersionSelect.value;
        const tagFilter = filterTagSelect.value;

        // Use search endpoint if filtering by tag, otherwise use list endpoint
        if (tagFilter) {
            const searchData = {
                tag_ids: [parseInt(tagFilter, 10)],
                status: statusFilter || undefined,
                fix_version_id: fixVersionFilter
                    ? parseInt(fixVersionFilter, 10)
                    : undefined,
            };
            tickets = await apiRequest<Ticket[]>(
                `/projects/${currentProjectId}/tickets/search`,
                {
                    method: "POST",
                    body: JSON.stringify(searchData),
                }
            );
        } else {
            let url = `/projects/${currentProjectId}/tickets`;
            const params = new URLSearchParams();
            if (statusFilter) params.set("status", statusFilter);
            if (fixVersionFilter)
                params.set("fix_version_id", fixVersionFilter);
            if (params.toString()) url += `?${params.toString()}`;
            tickets = await apiRequest<Ticket[]>(url);
        }

        sortTickets();
        renderTicketsTable();
    } catch (error) {
        ticketsContainer.innerHTML = `<div class="error">Failed to load tickets: ${error instanceof Error ? error.message : "Unknown error"}</div>`;
    }
}

function getVisibleTickets(): Ticket[] {
    // If a specific version is selected in the dropdown, show all (API already filtered)
    if (filterFixVersionSelect.value) {
        return tickets;
    }
    // Otherwise, apply version visibility filter client-side
    if (hiddenVersionIds.length === 0) {
        return tickets;
    }
    return tickets.filter((t) => {
        const versionId = t.fix_version_id ?? 0; // 0 = "No Version"
        return !hiddenVersionIds.includes(versionId);
    });
}

// Sorting
function sortTickets(): void {
    tickets.sort((a, b) => {
        let comparison = 0;

        switch (sortColumn) {
            case "identifier": {
                // Extract numeric part from identifier (e.g., "PROJ-123" -> 123)
                const numA = parseInt(a.identifier.split("-")[1] || "0", 10);
                const numB = parseInt(b.identifier.split("-")[1] || "0", 10);
                comparison = numA - numB;
                break;
            }
            case "status": {
                const statusOrder: TicketStatus[] = [
                    "todo",
                    "plan_in_review",
                    "planned",
                    "in_development",
                    "in_review",
                    "dev_done",
                    "testing_done",
                    "accepted_for_release",
                ];
                comparison =
                    statusOrder.indexOf(a.status) -
                    statusOrder.indexOf(b.status);
                break;
            }
            case "created_at":
                comparison =
                    new Date(a.created_at).getTime() -
                    new Date(b.created_at).getTime();
                break;
            case "updated_at":
                comparison =
                    new Date(a.updated_at).getTime() -
                    new Date(b.updated_at).getTime();
                break;
        }

        return sortDirection === "asc" ? comparison : -comparison;
    });
}

function toggleSort(column: SortColumn): void {
    if (sortColumn === column) {
        sortDirection = sortDirection === "asc" ? "desc" : "asc";
    } else {
        sortColumn = column;
        sortDirection =
            column === "created_at" || column === "updated_at" ? "desc" : "asc";
    }
    sortTickets();
    renderTicketsTable();
}

function getSortIndicator(column: SortColumn): string {
    if (sortColumn !== column) return "";
    return sortDirection === "asc" ? " ▲" : " ▼";
}

function renderTicketsTable(): void {
    const visibleTickets = getVisibleTickets();

    if (visibleTickets.length === 0) {
        if (tickets.length > 0 && hiddenVersionIds.length > 0) {
            ticketsContainer.innerHTML =
                '<div class="empty-state"><p>All tickets are hidden by version visibility settings.</p></div>';
        } else {
            ticketsContainer.innerHTML =
                '<div class="empty-state"><p>No tickets found. Click "New Ticket" to create one.</p></div>';
        }
        return;
    }

    const tableRows = visibleTickets
        .map((ticket) => {
            const fixVersion = projectFixVersions.find(
                (fv) => fv.id === ticket.fix_version_id
            );
            const tagsHtml = ticket.tags
                .map((t) => `<span class="tag">${escapeHtml(t.name)}</span>`)
                .join(" ");

            return `
                <tr class="ticket-row" data-ticket-id="${ticket.id}">
                    <td class="ticket-identifier-cell">
                        <a href="/pages/ticket-view.html?projectId=${currentProjectId}&ticketId=${ticket.id}" class="ticket-link" data-ticket-id="${ticket.id}">${escapeHtml(ticket.identifier)}</a>
                    </td>
                    <td class="ticket-title-cell"><a href="/pages/ticket-view.html?projectId=${currentProjectId}&ticketId=${ticket.id}" class="ticket-title-link" data-ticket-id="${ticket.id}">${escapeHtml(ticket.title)}</a></td>
                    <td class="ticket-status-cell">
                        <span class="status-badge status-${ticket.status}">${STATUS_LABELS[ticket.status]}</span>
                    </td>
                    <td class="ticket-version-cell">${fixVersion ? escapeHtml(fixVersion.version_number) : "-"}</td>
                    <td class="ticket-tags-cell">${tagsHtml || "-"}</td>
                    <td class="ticket-date-cell">${formatDateShort(ticket.created_at)}</td>
                    <td class="ticket-date-cell">${formatDateShort(ticket.updated_at)}</td>
                    <td class="ticket-actions-cell">
                        <button type="button" class="outline secondary edit-btn" data-ticket-id="${ticket.id}" title="Edit">Edit</button>
                        <button type="button" class="outline delete-action delete-btn" data-ticket-id="${ticket.id}" title="Delete">Delete</button>
                    </td>
                </tr>
            `;
        })
        .join("");

    ticketsContainer.innerHTML = `
        <table class="tickets-table">
            <thead>
                <tr>
                    <th class="sortable" data-column="identifier">ID${getSortIndicator("identifier")}</th>
                    <th>Title</th>
                    <th class="sortable" data-column="status">Status${getSortIndicator("status")}</th>
                    <th>Version</th>
                    <th>Tags</th>
                    <th class="sortable" data-column="created_at">Created${getSortIndicator("created_at")}</th>
                    <th class="sortable" data-column="updated_at">Updated${getSortIndicator("updated_at")}</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                ${tableRows}
            </tbody>
        </table>
    `;

    // Add sort click listeners
    ticketsContainer.querySelectorAll(".sortable").forEach((th) => {
        th.addEventListener("click", () => {
            const column = (th as HTMLElement).dataset.column as SortColumn;
            toggleSort(column);
        });
    });

    // Add ticket link listeners - navigate to view page
    // Allow Ctrl+Click / Cmd+Click / middle-click to open in new tab natively
    ticketsContainer.querySelectorAll(".ticket-link").forEach((link) => {
        link.addEventListener("click", (e) => {
            const mouseEvent = e as MouseEvent;
            if (mouseEvent.ctrlKey || mouseEvent.metaKey || mouseEvent.button === 1) return;
            e.preventDefault();
            const ticketId = parseInt(
                (link as HTMLElement).dataset.ticketId || "0",
                10
            );
            navigateToView(ticketId);
        });
    });

    // Add ticket title link listeners - navigate to view page
    ticketsContainer.querySelectorAll(".ticket-title-link").forEach((link) => {
        link.addEventListener("click", (e) => {
            const mouseEvent = e as MouseEvent;
            if (mouseEvent.ctrlKey || mouseEvent.metaKey || mouseEvent.button === 1) return;
            e.preventDefault();
            const ticketId = parseInt(
                (link as HTMLElement).dataset.ticketId || "0",
                10
            );
            navigateToView(ticketId);
        });
    });

    // Add edit button listeners
    ticketsContainer.querySelectorAll(".edit-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            const ticketId = parseInt(
                (btn as HTMLElement).dataset.ticketId || "0",
                10
            );
            navigateToEditor(ticketId);
        });
    });

    // Add delete button listeners
    ticketsContainer.querySelectorAll(".delete-btn").forEach((btn) => {
        btn.addEventListener("click", (e) => {
            e.stopPropagation();
            const ticketId = parseInt(
                (btn as HTMLElement).dataset.ticketId || "0",
                10
            );
            const ticket = tickets.find((t) => t.id === ticketId);
            if (ticket) openDeleteDialog(ticket);
        });
    });
}

// Filters
function applyFilters(): void {
    loadTickets();
}

function clearFilters(): void {
    filterStatusSelect.value = "";
    filterFixVersionSelect.value = "";
    filterTagSelect.value = "";
    // Version visibility is NOT reset — it's a persistent preference
    loadTickets();
}

// Delete dialog
let ticketToDelete: Ticket | null = null;

function openDeleteDialog(ticket: Ticket): void {
    ticketToDelete = ticket;
    deleteTicketName.textContent = `${ticket.identifier} - ${ticket.title}`;
    deleteTicketDialog.showModal();
}

function closeDeleteDialog(): void {
    deleteTicketDialog.close();
    ticketToDelete = null;
}

async function deleteTicket(): Promise<void> {
    if (!currentProjectId || !ticketToDelete) return;

    deleteTicketConfirmBtn.setAttribute("aria-busy", "true");
    deleteTicketConfirmBtn.disabled = true;

    try {
        await apiRequest(
            `/projects/${currentProjectId}/tickets/${ticketToDelete.id}`,
            {
                method: "DELETE",
            }
        );

        const deletedId = ticketToDelete.id;
        closeDeleteDialog();

        // Remove from local state
        tickets = tickets.filter((t) => t.id !== deletedId);

        renderTicketsTable();
    } catch (error) {
        alert(
            `Failed to delete ticket: ${error instanceof Error ? error.message : "Unknown error"}`
        );
    } finally {
        deleteTicketConfirmBtn.removeAttribute("aria-busy");
        deleteTicketConfirmBtn.disabled = false;
    }
}

// Tags dialog
function openTagsDialog(): void {
    renderTagsList();
    newTagNameInput.value = "";
    tagsDialog.showModal();
}

function closeTagsDialog(): void {
    tagsDialog.close();
    // Refresh selects after potentially modifying tags
    populateFilterSelects();
}

function renderTagsList(): void {
    if (projectTags.length === 0) {
        tagsList.innerHTML = '<p class="muted">No tags yet</p>';
        return;
    }

    tagsList.innerHTML = projectTags
        .map(
            (tag) => `
            <div class="tag-item">
                <span>${escapeHtml(tag.name)}</span>
                <button type="button" class="outline delete-action delete-tag-btn" data-tag-id="${tag.id}">Delete</button>
            </div>
        `
        )
        .join("");

    tagsList.querySelectorAll(".delete-tag-btn").forEach((btn) => {
        btn.addEventListener("click", async () => {
            const tagId = parseInt(
                (btn as HTMLElement).dataset.tagId || "0",
                10
            );
            await deleteTag(tagId);
        });
    });
}

async function addTag(): Promise<void> {
    if (!currentProjectId) return;

    const name = newTagNameInput.value.trim();
    if (!name) return;

    addTagBtn.setAttribute("aria-busy", "true");
    addTagBtn.disabled = true;

    try {
        await apiRequest(`/projects/${currentProjectId}/tags`, {
            method: "POST",
            body: JSON.stringify({ name }),
        });
        newTagNameInput.value = "";
        await loadTags();
        renderTagsList();
    } catch (error) {
        alert(
            `Failed to create tag: ${error instanceof Error ? error.message : "Unknown error"}`
        );
    } finally {
        addTagBtn.removeAttribute("aria-busy");
        addTagBtn.disabled = false;
    }
}

async function deleteTag(tagId: number): Promise<void> {
    if (!currentProjectId) return;

    if (!confirm("Are you sure you want to delete this tag?")) return;

    try {
        await apiRequest(`/projects/${currentProjectId}/tags/${tagId}`, {
            method: "DELETE",
        });
        await loadTags();
        renderTagsList();
    } catch (error) {
        alert(
            `Failed to delete tag: ${error instanceof Error ? error.message : "Unknown error"}`
        );
    }
}

initTicketsPage();
